import React, { useState } from "react";
// import { useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  Grid,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  PointOfSale as POSIcon,
  Speed,
  PhoneAndroid,
  Analytics,
  Payment,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";

const features = [
  { icon: <Speed />, label: "高速会計", desc: "3タップで決済完了" },
  { icon: <PhoneAndroid />, label: "モバイル注文", desc: "QRで客席から注文" },
  { icon: <Analytics />, label: "売上分析", desc: "リアルタイム集計" },
  { icon: <Payment />, label: "多決済対応", desc: "現金・カード・QR" },
];

const LoginPage = () => {
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const { login } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const result = await login({ loginId, password });
      if (!result.success) {
        setError(result.message);
        return;
      }
    } catch (error) {
      console.error("ログインエラー:", error);
      setError(error.response?.data?.message || "ログインに失敗しました");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
      }}
    >
      {/* 左側: 価値提案セクション */}
      <Box
        sx={{
          flex: isMobile ? "none" : 1,
          background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
          color: "white",
          p: isMobile ? 3 : 6,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Box sx={{ maxWidth: 500, mx: "auto" }}>
          {/* ロゴ */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: isMobile ? 2 : 4 }}>
            <POSIcon sx={{ fontSize: isMobile ? 40 : 56 }} />
            <Box>
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700}>
                POS レジシステム
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                飲食店向けオールインワンPOS
              </Typography>
            </Box>
          </Box>

          {/* キャッチコピー */}
          {!isMobile && (
            <>
              <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
                売上向上と業務効率化を
                <br />
                ひとつのシステムで実現
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, mb: 4 }}>
                モバイルオーダー、キャッシュレス決済、売上分析まで。
                <br />
                すべての機能が月額無料で使えます。
              </Typography>
            </>
          )}

          {/* 機能ハイライト */}
          <Grid container spacing={isMobile ? 1 : 2}>
            {features.map((feature, index) => (
              <Grid item xs={6} key={index}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: isMobile ? 1 : 1.5,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.1)",
                  }}
                >
                  {feature.icon}
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {feature.label}
                    </Typography>
                    {!isMobile && (
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {feature.desc}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* 信頼性指標 */}
          {!isMobile && (
            <Box sx={{ mt: 4, display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Chip
                label="導入実績 500店舗+"
                sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
              />
              <Chip
                label="月間取引 10万件+"
                sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
              />
              <Chip
                label="稼働率 99.9%"
                sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* 右側: ログインフォーム */}
      <Box
        sx={{
          flex: isMobile ? 1 : "0 0 400px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          bgcolor: "#fafafa",
        }}
      >
        <Container maxWidth="xs">
          <Paper
            elevation={isMobile ? 0 : 3}
            sx={{
              p: 4,
              borderRadius: 3,
              bgcolor: "white",
            }}
          >
            <Typography
              variant="h5"
              component="h1"
              fontWeight={700}
              textAlign="center"
              gutterBottom
            >
              ログイン
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ mb: 3 }}
            >
              アカウント情報を入力してください
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <TextField
                margin="normal"
                required
                fullWidth
                id="loginId"
                label="ログインID"
                name="loginId"
                autoComplete="username"
                autoFocus
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="パスワード"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  borderRadius: 2,
                }}
                id="loginButton"
              >
                ログイン
              </Button>
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
              display="block"
              sx={{ mt: 2 }}
            >
              ログイン情報を忘れた場合は管理者にお問い合わせください
            </Typography>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default LoginPage;
